import React, { useEffect, useState, useRef, useCallback } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Settings, Save, Loader2, Upload, X, Facebook, Instagram, Youtube, Globe, Mail, Phone, MapPin, Image as ImageIcon, ZoomIn, ZoomOut, Move, Plus, Trash2, Link, ExternalLink, Check, Twitter, Share2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

export default function GeneralSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flyerGrades, setFlyerGrades] = useState([]);
  const [form, setForm] = useState({
    club_name: "",
    tagline: "",
    about: "",
    contact_email: "",
    contact_phone: "",
    location: "",
    club_logo_url: "",
    hero_image_url: "",
    hero_image_x: 50,
    hero_image_y: 50,
    hero_image_scale: 1,
    hero_overlay_x: 50,
    hero_overlay_y: 50,
    hero_overlay_scale: 1,
    facebook_url: "",
    facebook_private_url: "",
    instagram_url: "",
    youtube_url: "",
    twitter_url: "",
    affiliations: [],
    team_selection_flyers: {},
    flyer_auto_post: { facebook: false, facebook_private: false, instagram: false, twitter: false },
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/landing");
        setForm((prev) => ({
          ...prev,
          club_name: data.club_name || "",
          tagline: data.tagline || "",
          about: data.about || "",
          contact_email: data.contact_email || "",
          contact_phone: data.contact_phone || "",
          location: data.location || "",
          club_logo_url: data.club_logo_url || "",
          hero_image_url: data.hero_image_url || "",
          hero_image_x: data.hero_image_x ?? 50,
          hero_image_y: data.hero_image_y ?? 50,
          hero_image_scale: data.hero_image_scale ?? 1,
          hero_overlay_x: data.hero_overlay_x ?? 50,
          hero_overlay_y: data.hero_overlay_y ?? 50,
          hero_overlay_scale: data.hero_overlay_scale ?? 1,
          facebook_url: data.facebook_url || "",
          facebook_private_url: data.facebook_private_url || "",
          instagram_url: data.instagram_url || "",
          youtube_url: data.youtube_url || "",
          twitter_url: data.twitter_url || "",
          affiliations: data.affiliations || [],
          team_selection_flyers: data.team_selection_flyers || {},
          flyer_auto_post: data.flyer_auto_post || { facebook: false, facebook_private: false, instagram: false, twitter: false },
        }));
      } catch (e) {
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
    // Fetch available grades/teams for flyer uploads
    api.get("/settings/selection-links").then(({ data: d }) => {
      setFlyerGrades(d?.grades_order || []);
    }).catch(() => {});
  }, []);

  const handleTaglineChange = (e) => {
    const raw = e.target.value;
    // Remove existing middle dots, uppercase everything, add middle dots between words
    const cleaned = raw.replace(/\s*·\s*/g, " ").toUpperCase();
    const words = cleaned.split(/\s+/).filter(Boolean);
    const formatted = words.join(" · ");
    setForm((prev) => ({ ...prev, tagline: formatted }));
  };

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Logo must be under 10MB");
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, w, h);
      const d = imageData.data;
      // Sample corner pixels to detect background colour
      const corners = [
        [0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1],
        [1, 1], [w - 2, 1], [1, h - 2], [w - 2, h - 2]
      ];
      let rSum = 0, gSum = 0, bSum = 0, count = 0;
      for (const [cx, cy] of corners) {
        const i = (cy * w + cx) * 4;
        rSum += d[i]; gSum += d[i + 1]; bSum += d[i + 2]; count++;
      }
      const bgR = Math.round(rSum / count);
      const bgG = Math.round(gSum / count);
      const bgB = Math.round(bSum / count);
      // Remove pixels within tolerance of the detected background
      const tolerance = 38;
      for (let i = 0; i < d.length; i += 4) {
        const dr = Math.abs(d[i] - bgR);
        const dg = Math.abs(d[i + 1] - bgG);
        const db = Math.abs(d[i + 2] - bgB);
        if (dr <= tolerance && dg <= tolerance && db <= tolerance) {
          d[i + 3] = 0; // set alpha to 0 (transparent)
        }
      }
      ctx.putImageData(imageData, 0, 0);
      // Use PNG to preserve transparency
      const result = canvas.toDataURL("image/png");
      const beforeKB = Math.round(file.size / 1024);
      const afterKB = Math.round((result.length * 3) / 4 / 1024);
      toast.success(`Logo processed: ${beforeKB}KB → ${afterKB}KB (background removed)`);
      setForm((prev) => ({ ...prev, club_logo_url: result }));
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      toast.error("Could not read image");
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const removeLogo = () => setForm((prev) => ({ ...prev, club_logo_url: "" }));

  const handleHeroUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Hero photo must be under 10MB");
      return;
    }
    const img = new Image();
    img.onload = () => {
      // Already the correct size — use the original file as-is (no quality loss)
      if (img.naturalWidth === 1920 && img.naturalHeight === 660) {
        const reader = new FileReader();
        reader.onload = (re) => {
          setForm((prev) => ({ ...prev, hero_image_url: re.target.result }));
          URL.revokeObjectURL(img.src);
          const sizeKB = Math.round(file.size / 1024);
          toast.success(`Hero photo set (${sizeKB}KB, already 1920×660)`);
        };
        reader.readAsDataURL(file);
        return;
      }
      // Different size — centre-crop and resize to 1920×660
      const canvas = document.createElement("canvas");
      canvas.width = 1920;
      canvas.height = 660;
      const ctx = canvas.getContext("2d");
      const srcAspect = img.naturalWidth / img.naturalHeight;
      const tgtAspect = 1920 / 660;
      let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
      if (srcAspect > tgtAspect) {
        sw = img.naturalHeight * tgtAspect;
        sx = (img.naturalWidth - sw) / 2;
      } else {
        sh = img.naturalWidth / tgtAspect;
        sy = (img.naturalHeight - sh) / 2;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, 1920, 660);
      const compressed = canvas.toDataURL("image/jpeg", 0.85);
      setForm((prev) => ({ ...prev, hero_image_url: compressed }));
      URL.revokeObjectURL(img.src);
      const sizeKB = Math.round((compressed.length * 3) / 4 / 1024);
      toast.success(`Hero resized to 1920×660 (${sizeKB}KB)`);
    };
    img.src = URL.createObjectURL(file);
  };

  const removeHero = () => setForm((prev) => ({ ...prev, hero_image_url: "" }));

  const handleFlyerUpload = (teamKey) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.includes("png") && !file.name.toLowerCase().endsWith(".png")) {
      toast.error("Only PNG images are allowed for flyer templates.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Flyer must be under 10MB");
      return;
    }

    const readAndUpload = async (dataUrl) => {
      try {
        await api.put(`/settings/flyer-template/${encodeURIComponent(teamKey)}`, { data_url: dataUrl });
        setForm((prev) => ({ ...prev, team_selection_flyers: { ...prev.team_selection_flyers, [teamKey]: dataUrl } }));
        toast.success(`Flyer saved for ${teamKey}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to save flyer");
      }
    };

    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (w !== 1080 || h !== 1350) {
        toast.error(`Image must be exactly 1080 × 1350 px. This is ${w} × ${h} px.`);
        URL.revokeObjectURL(img.src);
        return;
      }
      const reader = new FileReader();
      reader.onload = (re) => {
        URL.revokeObjectURL(img.src);
        readAndUpload(re.target.result);
      };
      reader.readAsDataURL(file);
    };
    img.onerror = () => {
      toast.error("Could not read image");
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  };

  const removeFlyer = async (teamKey) => {
    try {
      await api.delete(`/settings/flyer-template/${encodeURIComponent(teamKey)}`);
      setForm((prev) => {
        const updated = { ...prev.team_selection_flyers };
        delete updated[teamKey];
        return { ...prev, team_selection_flyers: updated };
      });
      toast.success(`Flyer removed for ${teamKey}`);
    } catch (err) {
      toast.error("Failed to remove flyer");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip flyer data from payload — flyers are saved individually on upload
      const { team_selection_flyers, ...rest } = form;
      await api.put("/landing", rest);
      toast.success("Settings saved");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to save settings";
      toast.error(typeof msg === "string" ? msg : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell variant="admin" title="General Settings">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell variant="admin" title="General Settings">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="general-settings-page">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
            System · General
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">General Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Club name, branding, contact info and social links.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-2">
          {/* Club Logo */}
          <AccordionItem value="logo" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="settings-logo-card">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-logo">
              <span className="flex items-center gap-2.5 text-sm font-semibold">
                <Upload className="h-4 w-4 text-[hsl(var(--accent))]" /> Club Logo
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Card className="p-5 rounded-2xl space-y-4 border-0 shadow-none">
                <p className="text-sm text-muted-foreground">Upload your club logo. It will be used across the app including the sidebar and public pages.</p>
                <div className="flex items-center gap-5">
                  {form.club_logo_url ? (
                    <div className="relative">
                      <div
                        className="h-24 w-24 rounded-xl border border-border overflow-hidden"
                        style={{
                          backgroundImage: "linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%), linear-gradient(-45deg, hsl(var(--muted)) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, hsl(var(--muted)) 75%), linear-gradient(-45deg, transparent 75%, hsl(var(--muted)) 75%)",
                          backgroundSize: "12px 12px",
                          backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0",
                        }}
                      >
                        <img
                          src={form.club_logo_url}
                          alt="Club logo"
                          className="h-full w-full object-contain"
                          data-testid="settings-logo-preview"
                        />
                      </div>
                      <button
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                        data-testid="settings-logo-remove"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-24 w-24 rounded-xl border-2 border-dashed border-border flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]">
                      <Upload className="h-6 w-6" />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="logo-upload" className="cursor-pointer">
                      <Button variant="outline" size="sm" className="rounded-full" asChild>
                        <span><Upload className="h-3.5 w-3.5 mr-1.5" /> {form.club_logo_url ? "Replace" : "Upload"} logo</span>
                      </Button>
                    </Label>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/png,.png"
                      className="hidden"
                      onChange={handleLogoUpload}
                      data-testid="settings-logo-input"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1.5">PNG, JPG or SVG. Max 10MB. Auto-compressed.</p>
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full px-6" data-testid="settings-save-logo">
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save
                  </Button>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Club Details */}
          <AccordionItem value="flyer" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="settings-flyer-card">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-flyer">
              <span className="flex items-center gap-2.5 text-sm font-semibold">
                <ImageIcon className="h-4 w-4 text-[hsl(var(--accent))]" /> Team Selection Flyers
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Card className="p-5 rounded-2xl space-y-5 border-0 shadow-none">
                <p className="text-sm text-muted-foreground">Upload a selection flyer for each team or grade. <strong>PNG only</strong>, 1080 × 1350 px (portrait).</p>

                {flyerGrades.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No teams or grades configured. Add them in PlayHQ → Team Selection Links.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {flyerGrades.map((teamName) => {
                      const flyerUrl = form.team_selection_flyers[teamName] || "";
                      const hasSaved = !!flyerUrl;
                      const hasPreview = flyerUrl && flyerUrl !== "__saved__" && (flyerUrl.startsWith("data:") || flyerUrl.startsWith("/api/"));
                      const inputId = `flyer-upload-${teamName.replace(/\s+/g, "-").toLowerCase()}`;
                      return (
                        <div key={teamName} className="space-y-2" data-testid={`flyer-slot-${teamName.replace(/\s+/g, "-").toLowerCase()}`}>
                          <p className="text-xs font-semibold truncate" title={teamName}>{teamName}</p>
                          <div className="relative">
                            {hasSaved ? (
                              <div className="relative">
                                <div className="aspect-[4/5] rounded-lg border border-border overflow-hidden bg-muted flex items-center justify-center">
                                  {hasPreview ? (
                                    <img src={flyerUrl.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${flyerUrl}` : flyerUrl} alt={`${teamName} flyer`} className="h-full w-full object-cover" />
                                  ) : (
                                    <div className="flex flex-col items-center text-green-600">
                                      <Check className="h-6 w-6 mb-1" />
                                      <span className="text-[10px] font-medium">Saved</span>
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => removeFlyer(teamName)}
                                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                                  data-testid={`flyer-remove-${teamName.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ) : (
                              <Label htmlFor={inputId} className="cursor-pointer block">
                                <div className="aspect-[4/5] rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))] hover:bg-muted transition-colors">
                                  <Upload className="h-5 w-5 mb-1" />
                                  <span className="text-[10px]">Upload</span>
                                </div>
                              </Label>
                            )}
                            <input
                              id={inputId}
                              type="file"
                              accept="image/png,.png"
                              className="hidden"
                              onChange={handleFlyerUpload(teamName)}
                            />
                            {hasSaved && (
                              <Label htmlFor={inputId} className="cursor-pointer block mt-1">
                                <span className="text-[10px] text-blue-600 hover:underline">Replace</span>
                              </Label>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <p className="text-xs text-muted-foreground">Flyers save automatically on upload</p>
                </div>

                {/* Auto-post to socials */}
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Share2 className="h-4 w-4 text-[hsl(var(--accent))]" />
                    <span className="text-sm font-semibold">Auto-post flyers to socials</span>
                  </div>
                  <p className="text-xs text-muted-foreground">When a team selection is published, the flyer will automatically be posted to the selected platforms.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { key: "facebook", label: "Facebook", icon: Facebook, needsUrl: "facebook_url" },
                      { key: "facebook_private", label: "Private Page", icon: Facebook, needsUrl: "facebook_private_url" },
                      { key: "instagram", label: "Instagram", icon: Instagram, needsUrl: "instagram_url" },
                      { key: "twitter", label: "X / Twitter", icon: Twitter, needsUrl: "twitter_url" },
                    ].map(({ key, label, icon: Icon, needsUrl }) => {
                      const checked = form.flyer_auto_post?.[key] || false;
                      const hasUrl = !!form[needsUrl];
                      return (
                        <div
                          key={key}
                          className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors ${checked ? "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5" : "border-border"}`}
                          data-testid={`flyer-social-${key}`}
                        >
                          <Switch
                            checked={checked}
                            onCheckedChange={(v) => {
                              setForm((prev) => ({
                                ...prev,
                                flyer_auto_post: { ...prev.flyer_auto_post, [key]: v },
                              }));
                            }}
                            className="scale-75"
                            data-testid={`flyer-social-switch-${key}`}
                          />
                          <Icon className="h-3.5 w-3.5 shrink-0" />
                          <span className="text-xs font-medium truncate">{label}</span>
                          {!hasUrl && <span className="text-[9px] text-amber-600 ml-auto">(no link)</span>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-end pt-1">
                    <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full px-6" data-testid="settings-save-flyer-socials">
                      {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                      Save
                    </Button>
                  </div>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Club Details */}
          <AccordionItem value="details" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="settings-details-card">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-details">
              <span className="flex items-center gap-2.5 text-sm font-semibold">
                <Settings className="h-4 w-4 text-[hsl(var(--accent))]" /> Club Details
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Card className="p-5 rounded-2xl space-y-4 border-0 shadow-none">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="club_name">Club Name</Label>
                    <Input id="club_name" value={form.club_name} onChange={handleChange("club_name")} placeholder="Derwent Cricket Club" data-testid="settings-club-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tagline">Tagline</Label>
                    <Input id="tagline" value={form.tagline} onChange={handleTaglineChange} placeholder="A short club motto or tagline" data-testid="settings-tagline" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="about">About</Label>
                  <Textarea id="about" value={form.about} onChange={handleChange("about")} rows={3} placeholder="Brief description of the club" data-testid="settings-about" />
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full px-6" data-testid="settings-save-details">
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save
                  </Button>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Contact Info */}
          <AccordionItem value="contact" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="settings-contact-card">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-contact">
              <span className="flex items-center gap-2.5 text-sm font-semibold">
                <Phone className="h-4 w-4 text-[hsl(var(--accent))]" /> Contact Information
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Card className="p-5 rounded-2xl space-y-4 border-0 shadow-none">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_email" className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</Label>
                    <Input id="contact_email" type="email" value={form.contact_email} onChange={handleChange("contact_email")} placeholder="club@example.com" data-testid="settings-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone" className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Phone</Label>
                    <Input id="contact_phone" value={form.contact_phone} onChange={handleChange("contact_phone")} placeholder="04XX XXX XXX" data-testid="settings-phone" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="location" className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Location</Label>
                    <Input id="location" value={form.location} onChange={handleChange("location")} placeholder="Ground address" data-testid="settings-location" />
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full px-6" data-testid="settings-save-contact">
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save
                  </Button>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Social Links */}
          <AccordionItem value="social" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="settings-social-card">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-social">
              <span className="flex items-center gap-2.5 text-sm font-semibold">
                <Globe className="h-4 w-4 text-[hsl(var(--accent))]" /> Social Links
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Card className="p-5 rounded-2xl space-y-4 border-0 shadow-none">
                <p className="text-sm text-muted-foreground">Leave empty to hide the icon on the public site.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="facebook_url" className="flex items-center gap-1.5"><Facebook className="h-3.5 w-3.5" /> Facebook</Label>
                    <Input id="facebook_url" value={form.facebook_url} onChange={handleChange("facebook_url")} placeholder="https://facebook.com/..." data-testid="settings-facebook" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="facebook_private_url" className="flex items-center gap-1.5"><Facebook className="h-3.5 w-3.5" /> Facebook Private Page</Label>
                    <Input id="facebook_private_url" value={form.facebook_private_url} onChange={handleChange("facebook_private_url")} placeholder="https://facebook.com/groups/..." data-testid="settings-facebook-private" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="instagram_url" className="flex items-center gap-1.5"><Instagram className="h-3.5 w-3.5" /> Instagram</Label>
                    <Input id="instagram_url" value={form.instagram_url} onChange={handleChange("instagram_url")} placeholder="https://instagram.com/..." data-testid="settings-instagram" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="youtube_url" className="flex items-center gap-1.5"><Youtube className="h-3.5 w-3.5" /> YouTube</Label>
                    <Input id="youtube_url" value={form.youtube_url} onChange={handleChange("youtube_url")} placeholder="https://youtube.com/..." data-testid="settings-youtube" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="twitter_url" className="flex items-center gap-1.5"><Twitter className="h-3.5 w-3.5" /> X / Twitter</Label>
                    <Input id="twitter_url" value={form.twitter_url} onChange={handleChange("twitter_url")} placeholder="https://x.com/..." data-testid="settings-twitter" />
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full px-6" data-testid="settings-save-social">
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save
                  </Button>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* News Settings */}
          <AccordionItem value="news" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="settings-news-card">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-news">
              <span className="flex items-center gap-2.5 text-sm font-semibold">
                <Settings className="h-4 w-4 text-[hsl(var(--accent))]" /> News Settings
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Card className="p-5 rounded-2xl space-y-4 border-0 shadow-none">
                <p className="text-sm text-muted-foreground">Configure how news items are displayed on the public landing page.</p>
                <div className="flex justify-end pt-2">
                  <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full px-6" data-testid="settings-save-news">
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save
                  </Button>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Affiliations */}
          <AccordionItem value="affiliations" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="settings-affiliations-card">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-affiliations">
              <span className="flex items-center gap-2.5 text-sm font-semibold">
                <Globe className="h-4 w-4 text-[hsl(var(--accent))]" /> Affiliations
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Card className="p-5 rounded-2xl space-y-4 border-0 shadow-none">
                <p className="text-sm text-muted-foreground">Manage affiliated associations and organisations displayed on the site.</p>

                {/* Existing affiliations */}
                <div className="space-y-3">
                  {(form.affiliations || []).map((aff, idx) => (
                    <div key={aff.name || `aff-${idx}`} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-background" data-testid={`affiliation-row-${idx}`}>
                      {/* Logo upload */}
                      <label className="w-12 h-12 rounded-lg border border-border bg-muted flex items-center justify-center shrink-0 overflow-hidden cursor-pointer hover:border-[hsl(var(--accent))] transition-colors group relative" data-testid={`affiliation-logo-upload-${idx}`}>
                        {aff.logo_url ? (
                          <img src={aff.logo_url} alt={aff.name || ""} className="w-full h-full object-contain p-1" />
                        ) : (
                          <Upload className="h-5 w-5 text-muted-foreground/40 group-hover:text-[hsl(var(--accent))] transition-colors" />
                        )}
                        <input
                          type="file"
                          accept="image/png,.png"
                          className="sr-only"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = () => {
                              const updated = [...form.affiliations];
                              updated[idx] = { ...updated[idx], logo_url: reader.result };
                              setForm((prev) => ({ ...prev, affiliations: updated }));
                            };
                            reader.readAsDataURL(file);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      {/* Fields */}
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Name (e.g. Cricket Australia)"
                          value={aff.name || ""}
                          onChange={(e) => {
                            const updated = [...form.affiliations];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setForm((prev) => ({ ...prev, affiliations: updated }));
                          }}
                          className="h-8 text-sm"
                          data-testid={`affiliation-name-${idx}`}
                        />
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <Link className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
                            <Input
                              placeholder="Website URL"
                              value={aff.website_url || ""}
                              onChange={(e) => {
                                const updated = [...form.affiliations];
                                updated[idx] = { ...updated[idx], website_url: e.target.value };
                                setForm((prev) => ({ ...prev, affiliations: updated }));
                              }}
                              className="h-8 text-sm pl-7"
                              data-testid={`affiliation-website-${idx}`}
                            />
                          </div>
                        </div>
                      </div>
                      {/* Delete */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          const updated = form.affiliations.filter((_, i) => i !== idx);
                          setForm((prev) => ({ ...prev, affiliations: updated }));
                        }}
                        data-testid={`affiliation-delete-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Add new */}
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => {
                    setForm((prev) => ({
                      ...prev,
                      affiliations: [...(prev.affiliations || []), { name: "", logo_url: "", website_url: "" }],
                    }));
                  }}
                  data-testid="affiliation-add-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Affiliation
                </Button>

                <div className="flex justify-end pt-2">
                  <Button onClick={handleSave} disabled={saving} size="sm" className="rounded-full px-6" data-testid="settings-save-affiliations">
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save
                  </Button>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </AppShell>
  );
}
